import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CreditCardRoundedIcon from "@mui/icons-material/CreditCardRounded";
import LocalShippingRoundedIcon from "@mui/icons-material/LocalShippingRounded";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";

const COLORS = {
  cream: "#F7F4EF",
  softCream: "#FBF9F6",
  white: "#FFFFFF",
  black: "#171717",
  mutedBlack: "#3A3A3A",
  grey: "#77736D",
  lightGrey: "#A29D95",
  border: "#DDD7CE",
  gold: "#977743",
  goldLight: "#C4A36A",
};

const PaymentOptionButton = styled(Box, {
  shouldForwardProp: (prop) => prop !== "selected",
})(({ selected }) => ({
  display: "flex",
  alignItems: "center",
  gap: 14,
  minHeight: 82,
  padding: "16px 18px",
  cursor: "pointer",
  borderRadius: 4,
  border: `1px solid ${selected ? COLORS.gold : COLORS.border}`,
  backgroundColor: selected ? COLORS.softCream : COLORS.white,
  boxShadow: selected ? `inset 0 -2px 0 ${COLORS.gold}` : "none",
  transition: "all 0.25s ease",
  "&:hover": {
    borderColor: COLORS.gold,
    backgroundColor: COLORS.softCream,
  },
}));

const StyledTextField = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    borderRadius: 3,
    backgroundColor: COLORS.white,
    color: COLORS.black,
    "& fieldset": {
      borderColor: COLORS.border,
    },
    "&:hover fieldset": {
      borderColor: COLORS.gold,
    },
    "&.Mui-focused fieldset": {
      borderColor: COLORS.black,
      borderWidth: 1,
    },
  },
  "& .MuiInputBase-input": {
    color: COLORS.black,
  },
  "& .MuiInputBase-input::placeholder": {
    color: COLORS.lightGrey,
    opacity: 1,
  },
});

const PrimaryButton = styled(Button)({
  minHeight: 52,
  borderRadius: 3,
  padding: "12px 24px",
  textTransform: "none",
  fontSize: 14,
  fontWeight: 700,
  letterSpacing: "0.3px",
  color: COLORS.white,
  backgroundColor: COLORS.black,
  boxShadow: "none",
  "&:hover": {
    backgroundColor: COLORS.gold,
    boxShadow: "none",
  },
  "&:disabled": {
    color: COLORS.white,
    backgroundColor: COLORS.lightGrey,
  },
});

const SectionLabel = ({ children }) => (
  <Typography
    variant="body2"
    sx={{
      mb: 1,
      fontWeight: 600,
      color: COLORS.mutedBlack,
      textAlign: "left",
    }}
  >
    {children}
  </Typography>
);

const PaymentSection = ({
  bookingData = {},
  onBookNow,
  onSuccess,
  onError,
}) => {
  const [paymentMethod, setPaymentMethod] = useState("cod");
  const [useShippingAddress, setUseShippingAddress] = useState(true);
  const [upiId, setUpiId] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [expiry, setExpiry] = useState("");
  const [cvv, setCvv] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const isTryOn = bookingData.bookingType === "tryOn";

  const formatCardNumber = (value) => {
    const numbers = value.replace(/\D/g, "").slice(0, 16);
    return numbers.replace(/(.{4})/g, "$1 ").trim();
  };

  const formatExpiry = (value) => {
    const numbers = value.replace(/\D/g, "").slice(0, 4);

    if (numbers.length > 2) {
      return `${numbers.slice(0, 2)}/${numbers.slice(2)}`;
    }

    return numbers;
  };

  const validatePayment = () => {
    if (isTryOn || paymentMethod === "cod") {
      return "";
    }

    if (paymentMethod === "card") {
      if (cardNumber.replace(/\s/g, "").length !== 16) {
        return "Please enter a valid 16-digit card number.";
      }

      if (!/^\d{2}\/\d{2}$/.test(expiry)) {
        return "Please enter a valid expiry date.";
      }

      if (!/^\d{3,4}$/.test(cvv)) {
        return "Please enter a valid CVV.";
      }
    }

    if (paymentMethod === "upi") {
      if (!/^[\w.-]+@[\w.-]+$/.test(upiId.trim())) {
        return "Please enter a valid UPI ID.";
      }
    }

    return "";
  };

  const handleBookNow = async () => {
    setErrorMessage("");

    const validationError = validatePayment();

    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    if (!onBookNow) {
      setErrorMessage("Booking callback is not configured.");
      return;
    }

    const requestBody = {
      userId: bookingData.userId,
      productId: String(bookingData.productId),
      paymentId: bookingData.paymentId || null,
      bookingDate: bookingData.bookingDate,
      bookingType: bookingData.bookingType,
      bookingTime: bookingData.bookingTime || null,
      deliveryOption: isTryOn
        ? "pickup"
        : bookingData.deliveryOption || "delivery",
      address: isTryOn ? null : bookingData.address || null,
    };

    setSubmitting(true);

    try {
      const response = await onBookNow(requestBody);

      onSuccess?.(response);
    } catch (error) {
      const backendMessage =
        error?.response?.data?.message ||
        error?.response?.data ||
        error?.message ||
        "Booking failed. Please try again.";

      const message =
        typeof backendMessage === "string"
          ? backendMessage
          : "Booking failed. Please try again.";

      setErrorMessage(message);
      onError?.(error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        width: "100%",
        maxWidth: 850,
        mx: "auto",
        p: { xs: 2, sm: 3.5 },
        borderRadius: 2,
        border: `1px solid ${COLORS.border}`,
        backgroundColor: COLORS.cream,
      }}
    >
      <Stack spacing={1} sx={{ mb: 3 }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            color: COLORS.black,
            letterSpacing: "-0.4px",
          }}
        >
          {isTryOn ? "Confirm Try-On Booking" : "Payment Details"}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: COLORS.grey,
            lineHeight: 1.7,
          }}
        >
          {isTryOn
            ? "Review your appointment details and confirm your try-on request."
            : "Choose your preferred payment method to complete your booking."}
        </Typography>
      </Stack>

      {errorMessage && (
        <Alert
          severity="error"
          onClose={() => setErrorMessage("")}
          sx={{
            mb: 3,
            borderRadius: 1,
          }}
        >
          {errorMessage}
        </Alert>
      )}

      {!isTryOn && (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <PaymentOptionButton
                selected={paymentMethod === "card"}
                onClick={() => setPaymentMethod("card")}
              >
                <CreditCardRoundedIcon
                  sx={{
                    color:
                      paymentMethod === "card"
                        ? COLORS.gold
                        : COLORS.grey,
                  }}
                />

                <Box>
                  <Typography
                    fontWeight={700}
                    fontSize={14}
                    color={COLORS.black}
                  >
                    Card
                  </Typography>

                  <Typography
                    variant="caption"
                    sx={{ color: COLORS.grey }}
                  >
                    Credit or debit
                  </Typography>
                </Box>
              </PaymentOptionButton>
            </Grid>

            <Grid item xs={12} sm={4}>
              <PaymentOptionButton
                selected={paymentMethod === "cod"}
                onClick={() => setPaymentMethod("cod")}
              >
                <LocalShippingRoundedIcon
                  sx={{
                    color:
                      paymentMethod === "cod"
                        ? COLORS.gold
                        : COLORS.grey,
                  }}
                />

                <Box>
                  <Typography
                    fontWeight={700}
                    fontSize={14}
                    color={COLORS.black}
                  >
                    Cash on Delivery
                  </Typography>

                  <Typography
                    variant="caption"
                    sx={{ color: COLORS.grey }}
                  >
                    Pay at delivery
                  </Typography>
                </Box>
              </PaymentOptionButton>
            </Grid>

            <Grid item xs={12} sm={4}>
              <PaymentOptionButton
                selected={paymentMethod === "upi"}
                onClick={() => setPaymentMethod("upi")}
              >
                <AccountBalanceWalletRoundedIcon
                  sx={{
                    color:
                      paymentMethod === "upi"
                        ? COLORS.gold
                        : COLORS.grey,
                  }}
                />

                <Box>
                  <Typography
                    fontWeight={700}
                    fontSize={14}
                    color={COLORS.black}
                  >
                    UPI
                  </Typography>

                  <Typography
                    variant="caption"
                    sx={{ color: COLORS.grey }}
                  >
                    Fast and secure
                  </Typography>
                </Box>
              </PaymentOptionButton>
            </Grid>
          </Grid>

          <Divider
            sx={{
              mb: 3,
              borderColor: COLORS.border,
            }}
          />

          {paymentMethod === "card" && (
            <Stack spacing={2.5}>
              <Box>
                <SectionLabel>Card number*</SectionLabel>

                <StyledTextField
                  fullWidth
                  size="small"
                  placeholder="1234 5678 9012 3456"
                  value={cardNumber}
                  onChange={(event) =>
                    setCardNumber(formatCardNumber(event.target.value))
                  }
                  inputProps={{ inputMode: "numeric" }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <CreditCardRoundedIcon
                          sx={{ color: COLORS.grey }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <SectionLabel>Expiration date*</SectionLabel>

                  <StyledTextField
                    fullWidth
                    size="small"
                    placeholder="MM/YY"
                    value={expiry}
                    onChange={(event) =>
                      setExpiry(formatExpiry(event.target.value))
                    }
                    inputProps={{ inputMode: "numeric" }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <SectionLabel>CVV*</SectionLabel>

                  <StyledTextField
                    fullWidth
                    size="small"
                    placeholder="123"
                    type="password"
                    value={cvv}
                    onChange={(event) =>
                      setCvv(
                        event.target.value
                          .replace(/\D/g, "")
                          .slice(0, 4)
                      )
                    }
                    inputProps={{ inputMode: "numeric" }}
                  />
                </Grid>
              </Grid>
            </Stack>
          )}

          {paymentMethod === "upi" && (
            <Stack spacing={2}>
              <SectionLabel>UPI ID*</SectionLabel>

              <StyledTextField
                fullWidth
                size="small"
                placeholder="yourname@bank"
                value={upiId}
                onChange={(event) => setUpiId(event.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AccountBalanceWalletRoundedIcon
                        sx={{ color: COLORS.grey }}
                      />
                    </InputAdornment>
                  ),
                }}
              />

              <Typography
                variant="caption"
                sx={{ color: COLORS.grey }}
              >
                Continue with your secure UPI payment flow.
              </Typography>
            </Stack>
          )}

          {paymentMethod === "cod" && (
            <Box
              sx={{
                p: 2.5,
                borderRadius: 2,
                backgroundColor: COLORS.softCream,
                border: `1px solid ${COLORS.border}`,
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <LocalShippingRoundedIcon
                  sx={{ color: COLORS.gold }}
                />

                <Box>
                  <Typography
                    fontWeight={700}
                    fontSize={14}
                    color={COLORS.black}
                  >
                    Cash on Delivery selected
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{ color: COLORS.grey, mt: 0.5 }}
                  >
                    Pay when your order is delivered.
                  </Typography>
                </Box>
              </Stack>
            </Box>
          )}

          <FormControlLabel
            sx={{
              mt: 2,
              color: COLORS.mutedBlack,
            }}
            control={
              <Checkbox
                checked={useShippingAddress}
                onChange={(event) =>
                  setUseShippingAddress(event.target.checked)
                }
                size="small"
                sx={{
                  color: COLORS.gold,
                  "&.Mui-checked": {
                    color: COLORS.gold,
                  },
                }}
              />
            }
            label={
              <Typography variant="body2">
                Use shipping address as billing address
              </Typography>
            }
          />
        </>
      )}

      {isTryOn && (
        <Box
          sx={{
            p: 2.5,
            borderRadius: 2,
            backgroundColor: COLORS.softCream,
            border: `1px solid ${COLORS.border}`,
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <CheckCircleRoundedIcon
              sx={{ color: COLORS.gold }}
            />

            <Box>
              <Typography
                fontWeight={700}
                fontSize={14}
                color={COLORS.black}
              >
                No payment required for Try-On
              </Typography>

              <Typography
                variant="body2"
                sx={{ color: COLORS.grey, mt: 0.5 }}
              >
                Confirm your request to notify the product owner.
              </Typography>
            </Box>
          </Stack>
        </Box>
      )}

      <Box
        sx={{
          mt: 3,
          pt: 3,
          borderTop: `1px solid ${COLORS.border}`,
        }}
      >
        <PrimaryButton
          fullWidth
          variant="contained"
          onClick={handleBookNow}
          disabled={submitting}
          startIcon={
            submitting ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <LockRoundedIcon fontSize="small" />
            )
          }
        >
          {submitting
            ? "Processing..."
            : isTryOn
              ? "Confirm Try-On Booking"
              : "Proceed to Pay"}
        </PrimaryButton>

        <Stack
          direction="row"
          spacing={1}
          justifyContent="center"
          alignItems="center"
          sx={{ mt: 2 }}
        >
          <LockRoundedIcon
            sx={{ fontSize: 14, color: COLORS.grey }}
          />

          <Typography
            variant="caption"
            sx={{ color: COLORS.grey }}
          >
            Your information is handled securely
          </Typography>
        </Stack>
      </Box>
    </Paper>
  );
};

export default PaymentSection;